// The phone frame's state, shared by the Dock's pieces: whether it is on, what
// the page put on the Dock, the way up, and the menu.
import {
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
} from "react";
import type { DockUpProps, PhoneMenu } from "../shell/phone";
import type { DockEntry } from "./items";

/** Where the menu's surface sits: its right and bottom edges from the frame's (px, a CSS length), and its width. */
export interface MenuBox {
  right: number;
  bottom: string;
  width: number;
}

/** The Dock's menu: closed, the Dock opened from the round button, or the panel above it. */
export type MenuState = null | {
  /** Opened from the round button (the Dock grows from it) or the resting Dock's "⋯". */
  from: "button" | "resting";
  /**
   * The button pressed: the menu grows out of it and folds back into it
   * while it is still there. Not `menuButton`, the last one still there: on
   * a chat that was the resting Dock's "⋯" (hidden under the post box's row,
   * 12 px lower), and the menu folded into that Dock and sank (the person,
   * 2026-10-19).
   */
  opener: HTMLElement;
  /** The panel above the Dock's row is open. */
  panel: boolean;
  /** Opened by a finger or the mouse (the focus stays off the items). */
  pointer: boolean;
  /** Over the Dock it opened from (measured when it opened). */
  box: MenuBox;
  /** The screen it opened on: going elsewhere closes it. */
  path: string | undefined;
};

export interface PhoneState {
  /** At a phone's width (the frame draws nothing of its own otherwise). */
  active: boolean;
  /**
   * A host draws the Dock (a native app): a page's buttons go to it as data
   * (`DockButtonProps.tool`) instead of being drawn.
   */
  hostDraws: boolean;
  menu: PhoneMenu;
  /** Where the Dock's way up (to the parent) goes, or null for none. */
  up: DockUpProps | null;
  /** How many page pieces hold the Dock, by kind. */
  claims: { cluster: number; tools: number; input: number };
  /** Where a page's one button is drawn (the left slot). */
  clusterHost: HTMLElement | null;
  /** Where a page's tools are drawn (beside the folded Dock). */
  toolsHost: HTMLElement | null;
  /** The Dock's menu. */
  menuState: MenuState;
  /** A text field outside the Dock has the keyboard: the Dock steps away. */
  away: boolean;
  /**
   * Something over the screen asked the Dock to step aside (`stow`): its row
   * sinks away and cannot be touched, keeping its room.
   */
  stowed: boolean;
  /** Every entry (Me's and the place's, in the kept order). */
  all: readonly DockEntry[];
  /** The entry of the screen shown now. */
  here: DockEntry | null;
  /** The button the menu grows from and gives the focus back to. */
  menuButton: RefObject<HTMLButtonElement | null>;
  /**
   * The ref for each button that opens the menu (the resting Dock's More, the
   * round button, a post box's round button). One ref object shared by them
   * was left empty when one of them went (a chat's round button on going back
   * to the list), and More then did nothing until the app was opened again
   * (2026-10-18). This keeps the last one still there.
   */
  holdMenuButton(el: HTMLButtonElement | null): (() => void) | undefined;
  /** Where what lies over the whole screen is drawn (overlay.tsx). */
  overlayHost: HTMLElement | null;
  /**
   * In place of the round button at a post box's right (an overlay's close
   * button). Absent: the round button.
   */
  end?: ReactNode;
  claim(kind: "cluster" | "tools" | "input"): () => void;
  /** Asks the Dock to step aside until the returned function is called. */
  stow(): () => void;
  /** Names the way up until the returned function is called (`DockUp`). */
  pushUp(up: DockUpProps): () => void;
  /** Opens the menu over the button that was pressed. */
  openMenu(
    from: "button" | "resting",
    button: HTMLElement,
    panel: boolean,
    pointer: boolean,
  ): void;
  togglePanel(): void;
  closeMenu(): void;
}

export const PhoneContext = createContext<PhoneState | null>(null);

export const usePhoneState = () => useContext(PhoneContext);

/** Inside a page's Dock whose buttons a host draws (they hand it their data). */
export const HostedDock = createContext(false);
